import React from "react";
import Card from "./Card";
import { useState, useEffect } from "react";
import { apiClient } from "../../../api";
import { baseUrl } from "../../../Environments/environment.js";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { Users, Scan, Bell, Home, Star, TrendingUp, Clock, CheckCircle } from "lucide-react";

const LoadingSkeleton = () => (
  <>
    {/* Summary Section Skeleton */}
    <div className="mt-8 relative overflow-hidden bg-gradient-to-r from-indigo-600/80 to-indigo-500/80 rounded-lg shadow-lg p-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="text-center">
            <div className="h-6 w-32 bg-indigo-300/30 rounded mx-auto mb-2"></div>
            <div className="h-10 w-20 bg-indigo-300/30 rounded mx-auto mb-2"></div>
            <div className="h-4 w-40 bg-indigo-300/30 rounded mx-auto"></div>
          </div>
        ))}
      </div>
    </div>

    <hr className="border-t border-gray-200 my-8" />

    {/* Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-indigo-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
              <div className="h-6 w-16 bg-gray-300 rounded"></div>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-3 w-full bg-gray-100 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  </>
);

const Overview = () => {
  const [analytics, setAnalytics] = useState({
    totalQrCodeScan: 0,
    totalServicesRequest: 0,
    totalHouseKeepingRequest: 0,
    totalInHouseGuest: 0,
    averageRating: 0,
    totalRevenue: 0,
    acceptedRequests: 0,
    totalRequests: 0,
    serviceEfficiency: 0
  });
  const [loading, setLoading] = useState(true);
  const { selectedPropertyId } = useProperty();

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        // Fetch basic analytics
        const response = await apiClient.get(
          `${baseUrl}/qrCodeScan/${selectedPropertyId}`,
        );
        console.log("QR Code Scan Response:", response.data.result);

        // Fetch all addon requests
        const addonsResponse = await apiClient.get(
          `${baseUrl}/guest/addons/${selectedPropertyId}?requestStatus=all`
        );
        
        // Get all requests
        const allRequests = addonsResponse.data.result.requests;
        
        // Filter regular addons and housekeeping requests
        const addonRequests = allRequests.filter(request => request.requestType !== "houseKeeping");
        const housekeepingRequests = allRequests.filter(request => request.requestType === "houseKeeping");

        // Calculate totals and accepted counts
        const totalAddons = addonRequests.length;
        const acceptedAddons = addonRequests.filter(request => request.requestStatus === "Accepted").length;
        const totalHousekeeping = housekeepingRequests.length;
        const acceptedHousekeeping = housekeepingRequests.filter(request => request.requestStatus === "Accepted").length;

        // Fetch in-house guests
        const params = {
          currentStatus: "Reservation" // This will get all bookings
        };

        const inHouseResponse = await apiClient.get(
          `${baseUrl}/guest/${selectedPropertyId}`,
          {
            params: params,
          }
        );
        console.log("In-house Response:", inHouseResponse.data.result.guests);

        // Update token if new one is provided
        if (inHouseResponse.headers.get("Authorization")) {
          const newToken = inHouseResponse.headers.get("Authorization");
          localStorage.setItem("accessToken", newToken.split(" ")[1]);
        }

        // Filter guests with status.currentStatus === "In House"
        const allGuests = inHouseResponse.data.result.guests || [];
        const inHouseGuests = allGuests.filter(guest => guest.status?.currentStatus === "In House");
        const totalInHouseGuests = inHouseGuests.length;

        console.log("All Guests Count:", allGuests.length);
        console.log("In-house Guests:", totalInHouseGuests);

        console.log("Regular Addons:", {
          total: totalAddons,
          accepted: acceptedAddons
        });
        console.log("Housekeeping:", {
          total: totalHousekeeping,
          accepted: acceptedHousekeeping
        });

        const totalRequests = totalAddons + totalHousekeeping;
        const acceptedRequests = acceptedAddons + acceptedHousekeeping;
        const serviceEfficiency = totalRequests > 0 ? Math.round((acceptedRequests / totalRequests) * 100) : 0;

        // Fetch reviews for ratings
        const reviewsResponse = await apiClient.get(
          `${baseUrl}/review/${selectedPropertyId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );

        // Calculate average rating
        let averageRating = 0;
        const reviews = reviewsResponse.data.result.reviews || [];
        if (reviews.length > 0) {
          const totalRating = reviews.reduce((sum, review) => sum + (review.overAllRating || 0), 0);
          averageRating = Number((totalRating / reviews.length).toFixed(1));
        }

        // Create new analytics object without spreading response.data.result
        const analyticsData = {
          totalQrCodeScan: response.data.result.length || 0,
          totalServicesRequest: totalAddons,
          totalHouseKeepingRequest: totalHousekeeping,
          totalInHouseGuest: totalInHouseGuests,
          averageRating,
          serviceEfficiency,
          totalRequests,
          acceptedRequests
        };

        console.log("Setting Analytics Data:", analyticsData);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error(error);
        setAnalytics(prev => ({
          ...prev,
          averageRating: 0,
          serviceEfficiency: 0,
          totalRequests: 0,
          acceptedRequests: 0,
          totalInHouseGuest: 0
        }));
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedPropertyId]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="sm:flex sm:items-center mt-6 animate-fadeIn">
          <div className="sm:flex-auto">
            <h1 className="text-2xl text-gray-800 tracking-tight">Analytics Dashboard</h1>
            <p className="mt-2 text-sm text-gray-600 leading-6">
              A comprehensive overview of your property's performance and guest activities.
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Summary Section */}
            <div className="mt-8 relative overflow-hidden bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-lg shadow-lg p-6 text-white animate-slideIn">
              <div className="absolute inset-0 bg-gradient-to-r from-white/[0.07] to-transparent opacity-20"></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600/30 to-indigo-500/30 blur-3xl"></div>
              <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Service Efficiency</p>
                    <p className="text-3xl mt-1 animate-numberRise">{analytics.serviceEfficiency}%</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      {analytics.acceptedRequests} of {analytics.totalRequests} requests accepted
                    </p>
                  </div>
                  <div className="text-center border-l border-r border-indigo-400 transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Total Requests</p>
                    <p className="text-3xl mt-1 animate-numberRise">{analytics.totalRequests}</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      Combined service requests
                    </p>
                  </div>
                  <div className="text-center transform hover:scale-105 transition-transform">
                    <p className="text-base text-indigo-100">Guest Satisfaction</p>
                    <p className="text-3xl mt-1 animate-numberRise">{analytics.averageRating}/5</p>
                    <p className="text-sm text-indigo-200 mt-1">
                      Average rating
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-t border-gray-200 my-8 animate-fadeIn" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-cardsAppear">
              <Card
                title="QR Code Scans"
                count={analytics.totalQrCodeScan}
                link="/dashboard/analytics/details?type=qrCode"
                icon={<Scan className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Service Requests"
                count={analytics.totalRequests}
                link="/dashboard/analytics/details?type=service"
                icon={<Bell className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Housekeeping Requests"
                count={analytics.totalHouseKeepingRequest}
                link="/dashboard/analytics/details?type=houseKeeping"
                icon={<Home className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="In-house Guests"
                count={analytics.totalInHouseGuest}
                link="/dashboard/analytics/details?type=currentInHouseGuests"
                icon={<Users className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Service Efficiency"
                count={`${analytics.serviceEfficiency}%`}
                subtitle={`${analytics.acceptedRequests} of ${analytics.totalRequests} requests accepted`}
                link="/dashboard/analytics/details?type=efficiency"
                icon={<CheckCircle className="w-8 h-8 text-indigo-600" />}
              />
              <Card
                title="Guest Satisfaction"
                count={`${analytics.averageRating}/5`}
                link="/dashboard/analytics/details?type=ratings"
                icon={<Star className="w-8 h-8 text-indigo-600" />}
              />
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes numberRise {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes cardsAppear {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: .5; }
          100% { opacity: 1; }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        .animate-slideIn {
          animation: slideIn 0.7s ease-out;
        }

        .animate-numberRise {
          animation: numberRise 1s ease-out;
        }

        .animate-cardsAppear {
          animation: cardsAppear 0.8s ease-out;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default Overview;
